import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { slotsService } from './slots.service';
import { Slot } from './slots.dto';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatTime } from '@/helpers';

const Slots = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSlots, setTotalSlots] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<number | null>(null);
  const itemsPerPage = 10;

  // Set up logout callback for session expired
  useEffect(() => {
    slotsService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      console.log(
        '⏱️ Debounce complete - setting debouncedSearchTerm to:',
        searchTerm
      );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch slots from API
  const fetchSlots = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    console.log(
      '🔄 Fetching slots - Page:',
      currentPage,
      'Search:',
      debouncedSearchTerm
    );

    try {
      setLoading(true);
      setError(null);

      const response = await slotsService.getSlots(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      });

      setSlots(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalSlots(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch slots');
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setSlotToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !slotToDelete) return;

    try {
      await slotsService.deleteSlotByDate(token, slotToDelete);
      showSnackbar('Slot deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSlotToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (slots.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchSlots();
      }
    } catch (err) {
      console.error('Error deleting slot:', err);
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete slot',
        'error'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSlotToDelete(null);
  };

  const handleAddSlot = () => {
    navigate('/slots/add');
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    console.log(
      '📄 Pagination clicked - Changing page from',
      currentPage,
      'to',
      page
    );
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const showSnackbar = useCallback(
    (message: string, severity: 'success' | 'error' = 'success') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    },
    []
  );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Check for success message from navigation state (e.g., after creating or editing a slot)
  useEffect(() => {
    const state = location.state as {
      message?: string;
      severity?: 'success' | 'error';
    } | null;
    if (state?.message) {
      showSnackbar(state.message, state.severity || 'success');
      // Clear the location state to prevent showing the message again on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showSnackbar]);

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Debug pagination state
  console.log(
    '🔢 Pagination State - loading:',
    loading,
    'totalPages:',
    totalPages,
    'currentPage:',
    currentPage,
    'totalSlots:',
    totalSlots
  );

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Slot Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSlot}
          disabled={!isAuthenticated}
        >
          Add Slot
        </Button>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search slots by service name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Date Range Filters */}
      {/* <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Start Date"
            type="date"
            value={startDateFilter}
            onChange={e => setStartDateFilter(e.target.value)}
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDateFilter}
            onChange={e => setEndDateFilter(e.target.value)}
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 150 }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || (!startDateFilter && !endDateFilter)}
            startIcon={<SearchIcon />}
          >
            Search
          </Button>
          {(appliedStartDate || appliedEndDate) && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearDateFilters}
              disabled={loading}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box> */}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Slots Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                {/* <TableCell>Bookings Per Slot</TableCell> */}
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading slots...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : slots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No slots found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                slots.map((slot, index) => (
                  <TableRow key={slot.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {slot.service?.title || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(slot.startDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(slot.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTime(slot.startTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTime(slot.endTime)}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Typography variant="body2">
                        {slot.bookingPerSlot}
                      </Typography>
                    </TableCell> */}
                    <TableCell>
                      <Chip
                        label={slot.status === 1 ? 'Active' : 'Inactive'}
                        color={slot.status === 1 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/slots/${slot.id}`)}
                          size="small"
                          disabled={loading}
                          title="View Slot"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/slots/edit/${slot.id}`)}
                          size="small"
                          disabled={loading}
                          title="Edit Slot"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(slot.id!)}
                          size="small"
                          disabled={loading}
                          title="Delete Slot"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages}`}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this slot? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Slots;
